// A record's history (ERP-74): what happened to it and to everything it is involved in — a
// customer's invoices, orders, payments and loans — with the notes, the calls and meetings logged,
// and the tasks given out about it and who did them.
import { post, relTime, useAction, useApi } from "@keel/core";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useRecordHref } from "./Notes";
import { Select } from "./Select";
import { Field, Icon, Modal } from "./ui";

export interface ActivityItem {
  at: string;
  kind: "event" | "note" | "call" | "meeting" | "email" | "visit" | "task" | "task_done";
  title: string;
  detail: string | null;
  actor: string | null;
  entityType: string;
  entityId: string;
  entityLabel: string | null;
  category: string;
  here: boolean;
}

export const ENGAGEMENTS = [
  { value: "call", label: "Call", icon: "send" },
  { value: "meeting", label: "Meeting", icon: "user" },
  { value: "email", label: "E-mail", icon: "send" },
  { value: "visit", label: "Visit", icon: "building" },
] as const;

const ICON: Record<ActivityItem["kind"], string> = {
  event: "history",
  note: "comment",
  call: "send",
  meeting: "user",
  email: "send",
  visit: "building",
  task: "timer",
  task_done: "check",
};

const FILTERS = [
  { value: "all", label: "Everything" },
  { value: "engagement", label: "Calls & meetings" },
  { value: "note", label: "Notes" },
  { value: "task", label: "Tasks" },
  { value: "event", label: "Documents" },
];

const matches = (f: string, k: ActivityItem["kind"]) =>
  f === "all" ||
  (f === "engagement" && ["call", "meeting", "email", "visit"].includes(k)) ||
  (f === "task" && (k === "task" || k === "task_done")) ||
  f === k;

/** Local `yyyy-mm-ddThh:mm`, what a datetime-local input wants. */
const nowLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

/** Write down a call, meeting, e-mail or visit that happened. */
export function LogEngagement({
  entityType,
  entityId,
  label,
  kind: initial = "call",
  onClose,
}: {
  entityType: string;
  entityId: string;
  label?: string | null;
  kind?: string;
  onClose: () => void;
}) {
  const { run, busy } = useAction();
  const [f, setF] = useState({ kind: initial, at: nowLocal(), body: "" });
  return (
    <Modal
      title="Log an engagement"
      width={520}
      onClose={onClose}
      busy={busy}
      submit="Log it"
      onSubmit={async () => {
        const ok = await run(
          () =>
            post("/notes", {
              entityType,
              entityId,
              entityLabel: label ?? null,
              kind: f.kind,
              occurredAt: new Date(f.at).toISOString(),
              body: f.body,
            }),
          "Logged",
        );
        if (ok) onClose();
      }}
    >
      <div className="form-grid">
        <Field label="What">
          <Select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
            {ENGAGEMENTS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="When">
          <input
            type="datetime-local"
            max={nowLocal()}
            value={f.at}
            onChange={(e) => setF({ ...f, at: e.target.value })}
          />
        </Field>
        <Field
          label="What was said or agreed"
          span
          tip="It goes into the history, and on the customer or supplier involved."
        >
          <textarea
            rows={4}
            value={f.body}
            placeholder="Called about the overdue invoice; they will pay on Friday."
            onChange={(e) => setF({ ...f, body: e.target.value })}
          />
        </Field>
      </div>
    </Modal>
  );
}

/** The history of a record, newest first, by day. */
export function Activity({
  entityType,
  entityId,
  label,
}: {
  entityType: string;
  entityId: string;
  label?: string | null;
}) {
  const q = useApi<ActivityItem[]>(`/activity?entityType=${entityType}&entityId=${entityId}`);
  const href = useRecordHref();
  const [filter, setFilter] = useState("all");
  const [log, setLog] = useState<string | null>(null);
  if (q.error) return <p className="muted pad">No history you can see here.</p>;
  if (!q.data) return <p className="muted pad">Loading…</p>;
  const items = q.data.filter((x) => matches(filter, x.kind));
  const days: [string, ActivityItem[]][] = [];
  for (const x of items) {
    const day = new Date(x.at).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    const last = days[days.length - 1];
    if (last && last[0] === day) last[1].push(x);
    else days.push([day, [x]]);
  }
  return (
    <div className="activity">
      <div className="activity-bar">
        <fieldset className="chips" aria-label="Show" style={{ border: 0, padding: 0, margin: 0 }}>
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              className={`chip ${filter === f.value ? "on" : ""}`}
              aria-pressed={filter === f.value}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </fieldset>
        <div className="row" style={{ gap: 6 }}>
          {ENGAGEMENTS.map((k) => (
            <button key={k.value} type="button" className="btn btn-sm" onClick={() => setLog(k.value)}>
              <Icon name={k.icon} size={13} />
              Log {k.label.toLowerCase()}
            </button>
          ))}
        </div>
      </div>
      {days.length === 0 ? (
        <p className="muted pad">Nothing here yet.</p>
      ) : (
        days.map(([day, list]) => (
          <section key={day}>
            <h4 className="activity-day">{day}</h4>
            <ul className="timeline">
              {list.map((x, n) => {
                const to = x.here ? null : href(x.entityType, x.entityId);
                return (
                  <li key={`${x.at}-${n}`} className={`activity-${x.kind}`}>
                    <span className="timeline-dot">
                      <Icon name={ICON[x.kind]} size={12} />
                    </span>
                    <div>
                      <div className="row-between">
                        <b>{to ? <Link to={to}>{x.title}</Link> : x.title}</b>
                        <span className="sm muted" title={new Date(x.at).toLocaleString()}>
                          {relTime(x.at)}
                          {x.actor ? ` · ${x.actor}` : ""}
                        </span>
                      </div>
                      {x.detail && <div className="timeline-text">{x.detail}</div>}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
      {log && (
        <LogEngagement
          entityType={entityType}
          entityId={entityId}
          label={label}
          kind={log}
          onClose={() => setLog(null)}
        />
      )}
    </div>
  );
}
